import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account } from '../models/account';
import { environment } from '../../environments/environment';
import { HttpResponseInterface } from '../interfaces/iHttpResponse';

@Injectable({
  providedIn: 'root'
})

export class AccountService {
    private account: Account;
    public accountChanged = new EventEmitter<any>();
    public notificationsChanged = new EventEmitter<any>();
    public practiceChanged = new EventEmitter<any>();
    constructor(private http: HttpClient) {
        this.account = this.getLocalAccount();
    }

    public recalcUserPoints():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/admin/regenerateUserPoints').pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getUserPaymentMethods():Observable<any>{
      return this.http.get(environment.apiEndpoint+`/api/account/paymentMethods`).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public addNewCard(number, expireMonth, expireYear, cvc):Observable<any>{
      return this.http.post(environment.apiEndpoint+`/api/account/paymentMethod`, {
        number:number,
        expireMonth:expireMonth,
        expireYear:expireYear,
        cvc:cvc
      }).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public removeCard(id):Observable<any>{
      return this.http.delete(environment.apiEndpoint+`/api/account/paymentMethod/${id}`).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getCPDRecord():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/account/cpdRecord').pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getCPDBlogRecord():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/account/cpdRecordBlog').pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public updatePoints(){
      this.getMyAccount().subscribe(res=>{
        this.setLocalAccount(res);
      });
    }

    public saveAccount(account: Account): Observable<any> {
        return this.http.put<HttpResponseInterface>(environment.apiEndpoint + `/api/user`, account).pipe(
            map(res => {
                this.setLocalAccount(res);
                this.accountChanged.emit();
                return res;
            })
        );
    }

    public saveCountryPreference(code: string): Observable<any> {
        return this.http.put(environment.apiEndpoint + '/api/user/country', { country_preference: code }).pipe(
            map(res => { return res; })
        );
    }

    public savePassword(newPassword:string, confirmPassword:string, currentPassword:string){
        return this.http.post<any>(environment.apiEndpoint + `/api/user/password`, {'password':newPassword, 'confirmPassword':confirmPassword, 'currentPassword':currentPassword}).pipe(
            map(res => {
                return res;
            })
        );
    }

    public recoverAccount(emailAddress:string){
        return this.http.get<any>(environment.apiEndpoint + `/api/user/recover/${emailAddress}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public getMyAccount():Observable<Account>{
        return this.http.get<any>(environment.apiEndpoint + `/api/user`).pipe(
            map(res => {
                return res.user;
            })
        );
    }

    public getNotifications():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/user/notifications').pipe(
        map(
          res=>{
            this.storeNotifications(res);
            return res;
          }
        )
      );
    }

    public storeNotifications(notifications){
      if (Object.keys(notifications).length!==0) {
        localStorage.setItem('notifications', JSON.stringify(notifications));
        this.notificationsChanged.emit();
      } else {
        this.clearNotifications();
      }
    }

    public clearNotifications(){
      localStorage.removeItem('notifications');
      this.notificationsChanged.emit();
    }

    public updatePractice(){
      this.practiceChanged.emit();
    }

    public getMyMentor():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/user/mentor').pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public setLocalAccount(account){
        localStorage.setItem('account',JSON.stringify(account));
        this.accountChanged.emit();
    }

    public getLocalAccount(){
        return JSON.parse(localStorage.getItem('account'));
    }

    public confirmPasswordReset(key, password, confirmPassword){
        return this.http.post<any>(environment.apiEndpoint + `/api/user/recover/confirm/${key}`, {'password':password, 'confirmPassword':confirmPassword}).pipe(
            map(res => {
                return res;
            })
        );
    }

    public completeRegistration(user:Account, token):Observable<any>{
        return this.http.post<any>(environment.apiEndpoint + `/api/register/${token}`, user).pipe(
            map(res => {
                return res;
            })
        );
    }

    public getByRegistrationToken(token):Observable<any>{
        return this.http.get<any>(environment.apiEndpoint + `/api/register/${token}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public checkUserCourseAccess(course_id:number){
        return this.http.get<any>(environment.apiEndpoint + `/api/user/access/${course_id}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public checkUserStreamAccess(stream_id:number){
      return this.http.get<any>(environment.apiEndpoint + `/api/user/access/s/${stream_id}`).pipe(
          map(res => {
              return res;
          })
      );
  }

    public getCurrentPoints():number{
        const localAccount = this.getLocalAccount();
        let points = 0;
        if(localAccount.points && localAccount.points.length!=0){
            var date = new Date();
            var year = date.getFullYear();

            localAccount.points.forEach(record=>{
                if(record.year==year){
                    points= record.points;
                }
            })
        }
        return points;
    }

    public getRoles():Observable<any>{
      return this.http.get(environment.apiEndpoint+'/api/userRoles').pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public applyForBacs(application:any, mailTo:string):Observable<any>{
      return this.http.post(environment.apiEndpoint+'/api/account/apply', {application: application, mailTo: mailTo}).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getBacsApplication():Observable<any>{
      return this.http.get(environment.apiEndpoint+`/api/account/application`).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }
}
